//frontend/src/App.js code
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import store from './store';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEffect, useState } from 'react';
import { loadUser } from './actions/userActions';
import { loadAdmin } from './actions/adminActions';
import NavigationTracker from './components/NavigationTracker';
import './utils/refreshUtils'; // Import refresh utilities for global access

// Layout Components
import Header from './components/layouts/Header';
import Footer from './components/layouts/Footer';

// User Components
import Home from './components/Home';
import Shop from './components/Shop';
import Login from './components/user/Login';
import Register from './components/user/Register';
import Profile from './components/user/Profile';
import UpdateProfile from './components/user/UpdateProfile';
import UpdatePassword from './components/user/UpdatePassword';
import ForgotPassword from './components/user/ForgotPassword';
import ResetPassword from './components/user/ResetPassword';

// Product Components
import ProductDetail from './components/product/ProductDetail';
import ProductSearch from './components/product/ProductSearch';
import ScrollToTop from './components/layouts/ScrollToTop';

// Cart & Checkout Components
import CartWithGuest from './components/cart/CartWithGuest';
import GuestCheckoutPage from './components/checkout/GuestCheckoutPage';
import Shipping from './components/cart/Shipping';
import ConfirmOrder from './components/cart/ConfirmOrder';
import Payment from './components/cart/Payment';
import Success from './components/cart/Success';
import ContactButtons from './components/layouts/ContactButtons';

// Order Components
import Orders from './components/order/Orders';
import OrderDetails from './components/order/OrderDetails';

// Admin Components
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminProfile from './components/admin/AdminProfile';
import AdminOrders from './components/admin/AdminOrders';
import AdminUsers from './components/admin/AdminUsers';
import AdminProducts from './components/admin/AdminProducts';
import NewProduct from './components/admin/NewProduct';
import EditProduct from './components/admin/EditProduct';
import AdminOrderDetails from './components/admin/AdminOrderDetails';
import UserView from './components/admin/UserView';

// Admin Delivery Staff Management
import CreateDeliveryStaff from './components/admin/CreateDeliveryStaff';
import DeliveryStaffList from './components/admin/DeliveryStaffList';
import DeliveryStaffDetail from './components/admin/DeliveryStaffDetail';
import CreateUser from './components/admin/CreateUser';
import EditUser from './components/admin/EditUser';
import EditDeliveryStaff from './components/admin/EditDeliveryStaff';

// Admin Order Management
import OnlinePayments from './components/admin/OnlinePayments';
import AdminDeliveredOrders from './components/admin/AdminDeliveredOrders';
import DeliveredOrderDetails from './components/admin/DeliveredOrderDetails';
import UnassignedOrders from './components/admin/UnassignedOrders';
import ReportGenerator from './components/admin/ReportGenerator';

// Admin Discount Management
import ProductDiscountManager from './components/admin/discount/ProductDiscount';
import BulkDiscountManager from './components/admin/discount/BulkDiscount';
import AllDiscounts from './components/admin/discount/AllDiscounts';

// Delivery Staff Components
import DeliveryLogin from './components/delivery/Login';
import DeliveryDashboard from './components/delivery/Dashboard';
import DeliveryLayout from './components/delivery/Layout';
import DeliveredOrders from './components/delivery/DeliveredOrders';
import DeliveryProfile from './components/delivery/Profile';

// Static Pages
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import FAQ from './components/pages/FAQ';
import About from './components/pages/About';
import Contact from './components/pages/Contact';
import Terms from './components/pages/Terms';
import RefundPolicy from './components/pages/RefundPolicy';
import NotFound from './components/NotFound';

// Route Protection Components
import ProtectedRoute from './components/route/ProtectedRoute';
import ProtectedAdminRoute from './components/route/ProtectedAdminRoute';
import ProtectedDeliveryRoute from './components/route/ProtectedDeliveryRoute';
import OrderProtectedRoute from './components/route/OrderProtectedRoute';



// Don't forget to import the components at the top of App.js
import AllCoupons from './components/admin/coupon/AllCoupons';
import CouponForm from './components/admin/coupon/CouponForm';

// RouteWrapper component
const RouteWrapper = () => {
    const location = useLocation();
    return location.state?.orderInfo ? <Success /> : <Navigate to="/cart" replace />;
};

// ConditionalContactButtons component
const ConditionalContactButtons = () => {
    const location = useLocation();
    const path = location.pathname;
    
    if (path.includes('/admin') || path.includes('/delivery')) {
        return null;
    }
    
    return <ContactButtons />;
};

// ConditionalFooter component
const ConditionalFooter = () => {
    const location = useLocation();
    const path = location.pathname;
    
    if (path.includes('/admin') || path.includes('/delivery')) {
        return null;
    }
    
    return <Footer />;
};

// Redux-connected content component
const ReduxConnectedContent = () => {
    const dispatch = useDispatch();
    const [authError, setAuthError] = useState(false);

    useEffect(() => {
        const loadUserData = async () => {
            try {
                const publicRoutes = ['/', '/shop', '/search', '/product', '/cart', '/guest-checkout', 
                                    '/login', '/register', '/password/forgot', '/privacy-policy', 
                                    '/terms', '/refund-policy', '/faq', '/about', '/contact'];
                
                const isPublicRoute = publicRoutes.some(route => 
                    window.location.pathname === route || 
                    window.location.pathname.startsWith(route + '/')
                );

                if (isPublicRoute) {
                    await dispatch(loadUser(true));
                } else {
                    await dispatch(loadUser(false));
                }
            } catch (err) {
                console.error('Failed to load user data:', err);
                setAuthError(true);
            }
        };

        const loadAdminData = async () => {
            try {
                if (window.location.pathname.includes('/admin')) {
                    await dispatch(loadAdmin());
                }
            } catch (err) {
                console.error('Failed to load admin data:', err);
            }
        };

        loadUserData();
        loadAdminData();
    }, [dispatch]);

    return (
        <div className="App">
            <HelmetProvider>
                <NavigationTracker />
                <ScrollToTop />
                <Header />
                <div className='container container-fluid p-0'>
                    <ToastContainer theme='dark' position="bottom-center" />
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<Home />} />
                        <Route path="/shop" element={<Shop />} />
                        <Route path="/search/:keyword" element={<Shop />} />
                        <Route path="/product/:id" element={<ProductDetail />} />
                        <Route path="/cart" element={<CartWithGuest />} />
                        <Route path="/guest-checkout" element={<GuestCheckoutPage />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/password/forgot" element={<ForgotPassword />} />
                        <Route path="/password/reset/:token" element={<ResetPassword />} />
                        
                        {/* Static Pages */}
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/refund-policy" element={<RefundPolicy />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        
                        {/* Protected User Routes */}
                        <Route path="/myprofile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                        <Route path="/myprofile/update" element={<ProtectedRoute><UpdateProfile /></ProtectedRoute>} />
                        <Route path="/myprofile/update/password" element={<ProtectedRoute><UpdatePassword /></ProtectedRoute>} />
                        
                        {/* Cart & Checkout Routes */}
                        <Route path="/shipping" element={
                            <ProtectedRoute>
                                <Shipping />
                            </ProtectedRoute>
                        } />
                        
                        <Route path="/order/confirm" element={
                            <OrderProtectedRoute>
                                <ConfirmOrder />
                            </OrderProtectedRoute>
                        } />
                        
                        <Route path="/payment" element={
                            <OrderProtectedRoute>
                                <Payment />
                            </OrderProtectedRoute>
                        } />
                        
                        <Route path="/success" element={<RouteWrapper />} />
                        
                        <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
                        <Route path="/order/:id" element={<ProtectedRoute><OrderDetails /></ProtectedRoute>} />
                        
                        {/* Admin Routes */}
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin/dashboard" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
                        <Route path="/admin/profile" element={<ProtectedAdminRoute><AdminProfile /></ProtectedAdminRoute>} />
                        <Route path="/admin/products" element={<ProtectedAdminRoute><AdminProducts /></ProtectedAdminRoute>} />
                        <Route path="/admin/product/new" element={<ProtectedAdminRoute><NewProduct /></ProtectedAdminRoute>} />
                        <Route path="/admin/product/:id/edit" element={<ProtectedAdminRoute><EditProduct /></ProtectedAdminRoute>} />
                        <Route path="/admin/orders" element={<ProtectedAdminRoute><AdminOrders /></ProtectedAdminRoute>} />
                        <Route path="/admin/order/:id" element={<ProtectedAdminRoute><AdminOrderDetails /></ProtectedAdminRoute>} />
                        <Route path="/admin/delivered-orders" element={<ProtectedAdminRoute><AdminDeliveredOrders /></ProtectedAdminRoute>} />
                        <Route path="/admin/delivered-order/:id" element={<ProtectedAdminRoute><DeliveredOrderDetails /></ProtectedAdminRoute>} />
                        <Route path="/admin/unassigned-orders" element={<ProtectedAdminRoute><UnassignedOrders /></ProtectedAdminRoute>} />
                        <Route path="/admin/online-payments" element={<ProtectedAdminRoute><OnlinePayments /></ProtectedAdminRoute>} />
                        <Route path="/admin/users" element={<ProtectedAdminRoute><AdminUsers /></ProtectedAdminRoute>} />
                        <Route path="/admin/user/:id" element={<ProtectedAdminRoute><UserView /></ProtectedAdminRoute>} />
                        <Route path="/admin/delivery-staff/new" element={<ProtectedAdminRoute><CreateDeliveryStaff /></ProtectedAdminRoute>} />
                        <Route path="/delivery-staff-list" element={<ProtectedAdminRoute><DeliveryStaffList /></ProtectedAdminRoute>} />
                        <Route path="/admin/delivery-staff/:id" element={<ProtectedAdminRoute><DeliveryStaffDetail /></ProtectedAdminRoute>} />
                        <Route path="/admin/discounts" element={<ProtectedAdminRoute><ProductDiscountManager /></ProtectedAdminRoute>} />
                        <Route path="/admin/bulk-discount" element={<ProtectedAdminRoute><BulkDiscountManager /></ProtectedAdminRoute>} />
                        <Route path="/admin/all-discounts" element={<ProtectedAdminRoute><AllDiscounts /></ProtectedAdminRoute>} />
                        <Route path="/admin/reports" element={<ProtectedAdminRoute><ReportGenerator /></ProtectedAdminRoute>} />
                        <Route path="/admin/user/create" element={<ProtectedAdminRoute><CreateUser /></ProtectedAdminRoute>} />
                        <Route path="/admin/user/edit/:id" element={<ProtectedAdminRoute><EditUser /></ProtectedAdminRoute>} />
                        <Route path="/admin/delivery-staff/edit/:id" element={<ProtectedAdminRoute><EditDeliveryStaff /></ProtectedAdminRoute>} />
                       
<Route path="/admin/coupons" element={<ProtectedAdminRoute><AllCoupons /></ProtectedAdminRoute>} />
<Route path="/admin/coupon/new" element={<ProtectedAdminRoute><CouponForm /></ProtectedAdminRoute>} />
<Route path="/admin/coupon/:id/edit" element={<ProtectedAdminRoute><CouponForm /></ProtectedAdminRoute>} />


                        {/* Delivery Staff Routes */}
                        <Route path="/delivery/login" element={<DeliveryLogin />} />
                        <Route path="/delivery/dashboard" element={
                            <ProtectedDeliveryRoute>
                                <DeliveryLayout><DeliveryDashboard /></DeliveryLayout>
                            </ProtectedDeliveryRoute>
                        } />
                        <Route path="/delivery/orders/delivered" element={
                            <ProtectedDeliveryRoute>
                                <DeliveryLayout><DeliveredOrders /></DeliveryLayout>
                            </ProtectedDeliveryRoute>
                        } />
                        <Route path="/delivery/profile" element={
                            <ProtectedDeliveryRoute>
                                <DeliveryLayout><DeliveryProfile /></DeliveryLayout>
                            </ProtectedDeliveryRoute>
                        } />
                        
                        {/* 404 Route */}
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </div>
                <ConditionalFooter />
                <ConditionalContactButtons />
            </HelmetProvider>
        </div>
    );
};

// Main App component
function App() {
    return (
        <Provider store={store}>
            <Router>
                <ReduxConnectedContent />
            </Router>
        </Provider>
    );
}

export default App;